import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import { useEffect, useContext } from "react";
import { io } from "socket.io-client";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link } from "react-router-dom";
import MyProjects from "./pages/MyProjects";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import Issues from "./pages/Issues";
import UsersManagement from './pages/UserManagement';
import ProtectedRoute from "./auth/ProtectedRoute";
import Landing from './pages/Landing';
import Layout from './components/Layout';
import { AuthProvider, AuthContext } from "./auth/AuthContext";
import AuthQueryCacheSync from "./auth/AuthQueryCacheSync";
import { departmentUsesBugTracker } from "./constants/departments";
import { MARKETING_PORTFOLIO_ROLES } from "./constants/marketingAccess";
import { USER_MGMT_CAN_MANAGE } from "./constants/userManagement";
import Budget from "./pages/Budget";
import ExpenseClaims from "./pages/ExpenseClaims";
import MyRoadmap from "./pages/MyRoadmap";
import ProjectDetail from "./pages/ProjectDetail";
import SprintBoard from "./pages/SprintBoard";
import DailyNotebook from "./pages/DailyNotebook";
import Tracker from "./pages/Tracker";
import MyTasks from "./pages/MyTasks";
import AIDraftReview from "./pages/AIDraftReview";
import EmployeeProfile from "./pages/EmployeeProfile";
import AcceptInvite from "./pages/AcceptInvite";
import Onboarding from "./pages/Onboarding";
import PendingApproval from "./pages/PendingApproval";
import Unauthorized from "./pages/Unauthorized";
import AccountSettings from "./pages/AccountSettings";
import ResetPassword from "./pages/ResetPassword";
import BitbucketAdmin from "./pages/BitbucketAdmin";
import DeveloperActivity from "./pages/DeveloperActivity";
import TeamActivity from "./pages/TeamActivity";
import TeamLeaveCalendar from "./pages/TeamLeaveCalendar";
import Notifications from "./pages/Notifications";
import SettingsPage from "./pages/SettingsPage";
import { getApiBaseUrl } from "./api/apiBaseUrl";
import CommandPalette from "./components/CommandPalette";
import { NavItemsProvider } from "./components/NavItemsContext";
import SsoCallback from "./pages/SsoCallback";

function resolveSocketUrl() {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  const base = getApiBaseUrl();
  if (base.startsWith("http://") || base.startsWith("https://")) {
    try {
      return new URL(base).origin;
    } catch {
      /* fall through */
    }
  }
  if (typeof window !== "undefined" && base === "/api") return window.location.origin;
  if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
    return window.location.origin;
  }
  if (typeof window !== "undefined") return "http://localhost:5000";
  return "http://localhost:5000";
}

const socket = io(resolveSocketUrl());

const ALL_APP_ROLES = ["superadmin", "admin", "hod", "manager", "marketing_manager", "brand_manager", "pr_manager", "product_manager", "lead", "product", "analyst", "developer", "tester", "designer", "marketing_designer", "devops", "member", "hr", "sales_marketing", "content_marketer", "trainee", "it_support", "hr_recruiter"];

/** Settings page is openable by anyone with email-edit OR matrix-edit permission. */
const SETTINGS_ACCESS = ["settings.email.edit", "permissions.matrix.edit"];

/** Marketing roles that may open Team Activity (PR Manager excluded) */
const MARKETING_TEAM_ACTIVITY_ROLES = ["marketing_manager", "brand_manager"];

/** All Projects + full project/financial list (not for individual contributors) */
const PROJECTS_DIRECTORY_ROLES = [
  "superadmin", "admin", "hod", "manager",
  ...MARKETING_PORTFOLIO_ROLES,
  "product_manager", "lead", "product", "analyst",
];

/** Manager-equivalent roles — user mgmt, profiles, AI planning */
const MANAGER_EQUIV_ROLES = [
  "superadmin", "admin", "hod", "manager",
  ...MARKETING_PORTFOLIO_ROLES,
  "product_manager", "lead", "product", "analyst", "hr",
];

/** Team Activity page — PR Manager excluded */
const TEAM_ACTIVITY_ROLES = [
  "superadmin", "admin", "hod", "manager",
  ...MARKETING_TEAM_ACTIVITY_ROLES,
  "product_manager", "lead", "product", "analyst", "hr",
];

/** Budget portfolio page */
const BUDGET_DIRECTORY_ROLES = [
  "superadmin", "admin", "hod", "manager",
  ...MARKETING_PORTFOLIO_ROLES,
  "product_manager", "lead", "product", "analyst",
];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function IssuesRoute() {
  const { user } = useContext(AuthContext);
  if (!departmentUsesBugTracker(user?.department)) {
    return <Navigate to="/sprint-board" replace />;
  }
  return (
    <Layout>
      <Issues />
    </Layout>
  );
}

/**
 * Which cached queries each server event makes stale.
 *
 * Invalidate only — never write the socket payload into the cache. The client
 * refetches through the normal authenticated REST API, so a message can't
 * surface data the viewer isn't allowed to see (socket connections carry no
 * auth handshake today).
 *
 * The global staleTime is 5 minutes, so without this a teammate's change can
 * sit invisible for that long — worst on Tracker, where the whole point is
 * seeing what's just been handed to you.
 */
const DASHBOARD_KEYS = ['dashboardCharts', 'dashboardStats'];
const ISSUE_KEYS = [...DASHBOARD_KEYS, 'board', 'my-issues'];
const SPRINT_KEYS = [...DASHBOARD_KEYS, 'board', 'sprints'];
const TRACKER_KEYS = ['trackerRequirements', 'trackerPortfolio', 'trackerMyStages'];

const SOCKET_EVENT_KEYS = {
  issueCreated: ISSUE_KEYS,
  issueUpdated: ISSUE_KEYS,
  issueDeleted: ISSUE_KEYS,
  issueComment: ['board', 'my-issues'],
  boardUpdated: ISSUE_KEYS,
  sprintCreated: SPRINT_KEYS,
  sprintUpdated: SPRINT_KEYS,
  sprintDeleted: SPRINT_KEYS,
  sprintReleased: SPRINT_KEYS,
  trackerRequirementCreated: TRACKER_KEYS,
  trackerRequirementUpdated: TRACKER_KEYS,
};

function App() {
  useEffect(() => {
    const handlers = Object.entries(SOCKET_EVENT_KEYS).map(([event, keys]) => {
      const handler = () => {
        keys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      };
      socket.on(event, handler);
      return [event, handler];
    });

    return () => {
      // Remove only our own handlers, so a future listener elsewhere on the
      // same shared socket isn't torn off by this cleanup.
      handlers.forEach(([event, handler]) => socket.off(event, handler));
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthQueryCacheSync />
        <Toaster
          position="top-center"
          containerStyle={{
            /* Above issue/task modals (9999) and sprint overlays (10000+) */
            zIndex: 20000,
          }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#2C63AE',
              color: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              fontSize: '0.95rem',
              fontWeight: '600',
              maxWidth: '420px',
            },
            success: { 
              style: { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              iconTheme: { primary: '#fff', secondary: '#059669' },
            },
            error: { 
              duration: 5000,
              style: { 
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                border: '1px solid #fecaca',
              },
              iconTheme: { primary: '#fff', secondary: '#dc2626' },
            },
          }}
        />
        {/* Inside the router (needs navigate) and inside AuthProvider (its
            destinations come from the sidebar's permission-filtered list). */}
        <NavItemsProvider>
        <CommandPalette />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* Public invite acceptance — no auth needed */}
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          {/* Public: Microsoft returns here before any session exists. */}
          <Route path="/sso/callback" element={<SsoCallback />} />
          
          {/* Public password reset — no auth needed */}
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Post–OTP profile (no sidebar) */}
          <Route path="/onboarding" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Onboarding />
            </ProtectedRoute>
          } />

          {/* Budget portfolio (replaces legacy Resource Allocation) */}
          <Route path="/budget" element={
            <ProtectedRoute allowedRoles={BUDGET_DIRECTORY_ROLES} anyPermission={["nav.budget"]}>
              <Layout><Budget /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/expense-claims" element={
            <ProtectedRoute
              allowedRoles={ALL_APP_ROLES}
              anyPermission={["nav.expense_claims", "nav.expense_claims.approvals"]}
            >
              <Layout><ExpenseClaims /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/resource-allocation" element={<Navigate to="/budget" replace />} />

          {/* NEW: Developer/Tester Roadmap */}
          <Route path="/roadmap" element={
            <ProtectedRoute allowedRoles={["developer", "tester"]}>
              <Layout><MyRoadmap /></Layout>
            </ProtectedRoute>
          } />

            {/* Dashboard (all roles) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/projects/:id" element={<ProtectedRoute allowedRoles={ALL_APP_ROLES}><Layout><ProjectDetail /></Layout></ProtectedRoute>} />
            {/* My Projects (tester/developer/member) */}
            <Route
              path="/my-projects"
              element={
                <ProtectedRoute allowedRoles={["developer", "tester", "member"]}>
                  <Layout>
                    <MyProjects />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* All Projects (leadership / PM — includes budget on API) */}
            <Route
              path="/projects"
              element={
                <ProtectedRoute allowedRoles={PROJECTS_DIRECTORY_ROLES} anyPermission={["nav.projects"]}>
                  <Layout>
                    <Projects />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Issues — Engineering department only (bug tracker) */}
            <Route
              path="/issues"
              element={
                <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
                  <IssuesRoute />
                </ProtectedRoute>
              }
            />
            {/* User Management — stewards with nav.users_management (not brand_manager) */}
          <Route
            path="/users-management"
            element={
              <ProtectedRoute allowedRoles={USER_MGMT_CAN_MANAGE}>
                <Layout>
                  <UsersManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

            {/* Pending Approval page - for users awaiting manager approval */}
            <Route path="/pending-approval" element={<PendingApproval />} />

            {/* Unauthorized page */}
            <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Sprint Board */}
          <Route path="/sprint-board" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><SprintBoard /></Layout>
            </ProtectedRoute>
          } />
          {/* Tracker — everyone can reach it; what they may do is decided per stage */}
          <Route path="/tracker" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES} anyPermission={["nav.tracker"]}>
              <Layout><Tracker /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/daily-notebook" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES} anyPermission={["nav.daily_notebook"]}>
              <Layout><DailyNotebook /></Layout>
            </ProtectedRoute>
          } />





          {/* AI Planning - Draft Review */}
          <Route path="/ai-planning/:sprintId" element={
            <ProtectedRoute allowedRoles={MANAGER_EQUIV_ROLES}>
              <Layout><AIDraftReview /></Layout>
            </ProtectedRoute>
          } />

          {/* My Tasks / My Bugs */}
          <Route path="/my-tasks" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><MyTasks /></Layout>
            </ProtectedRoute>
          } />

          {/* Employee Profile */}
          <Route path="/users/:id/profile" element={
            <ProtectedRoute allowedRoles={MANAGER_EQUIV_ROLES}>
              <Layout><EmployeeProfile /></Layout>
            </ProtectedRoute>
          } />

          {/* Legacy Releases page — hidden from nav; release info lives on All Projects */}
          <Route path="/releases" element={<Navigate to="/projects" replace />} />

          {/* Account Settings (all roles) */}
          <Route path="/account-settings" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><AccountSettings /></Layout>
            </ProtectedRoute>
          } />

          {/* Bitbucket Admin */}
          <Route path="/bitbucket-admin" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <Layout><BitbucketAdmin /></Layout>
            </ProtectedRoute>
          } />

          {/* Developer Activity */}
          <Route path="/developer-activity" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><DeveloperActivity /></Layout>
            </ProtectedRoute>
          } />

          {/* Team Activity - Manager/Admin View */}
          <Route path="/team-activity" element={
            <ProtectedRoute allowedRoles={TEAM_ACTIVITY_ROLES} anyPermission={["nav.team_activity"]}>
              <Layout><TeamActivity /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/team-leave" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><TeamLeaveCalendar /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/notifications" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES}>
              <Layout><Notifications /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute allowedRoles={ALL_APP_ROLES} anyPermission={SETTINGS_ACCESS}>
              <Layout><SettingsPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Backwards-compat: old direct link still works, opens Settings on the permissions tab. */}
          <Route path="/role-permissions" element={<Navigate to="/settings?tab=permissions" replace />} />

          {/* 404 fallback */}
          <Route path="*" element={
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <h1>404 - Page Not Found</h1>
              <Link to="/dashboard">Back to Dashboard</Link>
            </div>
          } />
        </Routes>
        </NavItemsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
